/** Attendance data hooks — V6-04 §4. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AttendanceChipView,
  AttendanceCorrectionStatus,
  AttendanceCorrectionView,
  AttendanceDayView,
  AttendanceMonthView,
  AttendanceRules,
  TeamAttendanceView,
  WhoIsInRow,
} from '@virtuetasks/types';
import { API_BASE, api, getAccessToken, qs } from '@/lib/api';
import { useAuth } from '@/features/auth';

export const ATTENDANCE_KEY = 'attendance';

/**
 * The chip polls as well as listening.
 *
 * The socket tells it about anything *this* person does elsewhere, but the
 * running total has to tick on its own — and the auto-close notice appears
 * from a job, with no event to carry it. A minute is far finer than either
 * needs and cheap: the endpoint reads one day of one person's sessions.
 *
 * `enabled` and `retry: false` are not decoration. This is a polling query on
 * a component that lives in the top bar of every page, so it is the one most
 * likely to be in flight at the moment somebody signs out. A 401 answered with
 * a retry drives the refresh-and-replay path, which then fails against a
 * revoked session and sends the app to /login a second time — landing on the
 * login form while a person (or a test) is already clicking it.
 */
export function useAttendanceChip() {
  const { status } = useAuth();
  return useQuery({
    queryKey: [ATTENDANCE_KEY, 'chip'],
    enabled: status === 'authenticated',
    retry: false,
    queryFn: async () => (await api.get<AttendanceChipView>('/attendance/chip')).data,
    refetchInterval: 60_000,
  });
}

function useAttendanceInvalidation() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [ATTENDANCE_KEY] });
  };
}

export interface StartWorkInput {
  note?: string;
  /** V10 §1 — "End previous session & start", as one request. */
  endPrevious?: boolean;
}

export function useStartWork() {
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: async (input: StartWorkInput = {}) => {
      const payload: StartWorkInput = {};
      if (input.note) payload.note = input.note;
      if (input.endPrevious) payload.endPrevious = true;
      return (await api.post('/attendance/start', payload)).data;
    },
    onSuccess: invalidate,
  });
}

export function useEndWork() {
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: async (note?: string) =>
      (await api.post('/attendance/end', note ? { note } : {})).data,
    onSuccess: invalidate,
  });
}

export function useAttendanceMonth(month: string, userId?: string) {
  return useQuery({
    queryKey: [ATTENDANCE_KEY, 'month', month, userId],
    queryFn: async () =>
      (await api.get<AttendanceMonthView>(`/attendance/month${qs({ month, userId })}`)).data,
  });
}

export function useAttendanceDay(date: string | null, userId?: string) {
  return useQuery({
    queryKey: [ATTENDANCE_KEY, 'day', date, userId],
    enabled: Boolean(date),
    queryFn: async () =>
      (await api.get<AttendanceDayView>(`/attendance/day${qs({ date, userId })}`)).data,
  });
}

export function useAttendanceRules() {
  return useQuery({
    queryKey: [ATTENDANCE_KEY, 'rules'],
    queryFn: async () => (await api.get<AttendanceRules>('/attendance/rules')).data,
  });
}

export function useSaveAttendanceRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<AttendanceRules>) =>
      (await api.put<AttendanceRules>('/attendance/rules', patch)).data,
    onSuccess: (rules) => qc.setQueryData([ATTENDANCE_KEY, 'rules'], rules),
  });
}

/* ── Corrections ───────────────────────────────────────── */

export interface CorrectionInput {
  date: string;
  sessionId?: string;
  proposedStartAt: string;
  proposedEndAt: string;
  reason: string;
}

export function useCorrections(filters: { status?: AttendanceCorrectionStatus; mine?: boolean } = {}) {
  return useQuery({
    queryKey: [ATTENDANCE_KEY, 'corrections', filters],
    queryFn: async () =>
      (
        await api.get<AttendanceCorrectionView[]>(
          `/attendance/corrections${qs({ ...filters, limit: 100 })}`,
        )
      ).data,
  });
}

export function useRequestCorrection() {
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: async (input: CorrectionInput) =>
      (await api.post<AttendanceCorrectionView>('/attendance/corrections', input)).data,
    onSuccess: invalidate,
  });
}

export function useDecideCorrection() {
  const invalidate = useAttendanceInvalidation();
  return useMutation({
    mutationFn: async ({
      id,
      action,
      decisionNote,
    }: {
      id: string;
      action: 'approve' | 'reject';
      decisionNote?: string;
    }) =>
      (
        await api.post<AttendanceCorrectionView>(
          `/attendance/corrections/${id}/${action}`,
          decisionNote ? { decisionNote } : {},
        )
      ).data,
    onSuccess: invalidate,
  });
}

/* ── Team report ───────────────────────────────────────── */

export function useTeamAttendance(month: string, enabled = true) {
  return useQuery({
    queryKey: [ATTENDANCE_KEY, 'team', month],
    enabled,
    queryFn: async () =>
      (await api.get<TeamAttendanceView>(`/attendance/team${qs({ month })}`)).data,
  });
}

/**
 * Download the month as CSV.
 *
 * Fetched with the bearer token and saved from a blob, not linked with an
 * `<a href>`: the access token lives in memory, so a plain link arrives
 * unauthenticated and the browser shows a JSON 401 where a spreadsheet should
 * be.
 */
export async function downloadTeamCsv(month: string): Promise<void> {
  const response = await fetch(`${API_BASE}/attendance/team/export${qs({ month })}`, {
    credentials: 'include',
    headers: getAccessToken() ? { authorization: `Bearer ${getAccessToken()}` } : {},
  });
  if (!response.ok) throw new Error(`Export failed (${response.status})`);

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `attendance-${month}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function useWhoIsIn(enabled = true) {
  return useQuery({
    queryKey: [ATTENDANCE_KEY, 'today'],
    enabled,
    refetchInterval: 60_000,
    queryFn: async () => (await api.get<WhoIsInRow[]>('/attendance/team/today')).data,
  });
}
